'use client';

import * as React from 'react';
import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { clsx } from 'clsx';
import styles from './SideSheet.module.css';

/* ===========================================
   SIDE SHEET ROOT
   =========================================== */

export interface SideSheetProps {
  /**
   * Whether the sheet is open
   */
  open?: boolean;

  /**
   * Default open state (uncontrolled)
   */
  defaultOpen?: boolean;

  /**
   * Callback when open state changes
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * Modal behavior
   * - true: Focus trap + scroll lock + pointer blocking
   * - 'trap-focus': Focus trapped, scroll/pointer enabled
   * - false: No restrictions
   */
  modal?: boolean | 'trap-focus';

  children?: React.ReactNode;
}

export function SideSheet({
  open,
  defaultOpen,
  onOpenChange,
  modal = true,
  children,
}: SideSheetProps) {
  return (
    <BaseDialog.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      modal={modal}
    >
      {children}
    </BaseDialog.Root>
  );
}

/* ===========================================
   SIDE SHEET TRIGGER
   =========================================== */

export interface SideSheetTriggerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  children?: React.ReactNode;
}

export const SideSheetTrigger = React.forwardRef<
  HTMLButtonElement,
  SideSheetTriggerProps
>(({ className, children, ...props }, ref) => (
  <BaseDialog.Trigger ref={ref} className={className} {...props}>
    {children}
  </BaseDialog.Trigger>
));

SideSheetTrigger.displayName = 'SideSheetTrigger';

/* ===========================================
   SIDE SHEET PORTAL
   =========================================== */

export interface SideSheetPortalProps {
  children?: React.ReactNode;
  container?: HTMLElement | null;
}

export function SideSheetPortal({ children, container }: SideSheetPortalProps) {
  return <BaseDialog.Portal container={container}>{children}</BaseDialog.Portal>;
}

/* ===========================================
   SIDE SHEET BACKDROP
   =========================================== */

export interface SideSheetBackdropProps {
  className?: string;
}

export function SideSheetBackdrop({ className }: SideSheetBackdropProps) {
  return <BaseDialog.Backdrop className={clsx(styles.backdrop, className)} />;
}

/* ===========================================
   SIDE SHEET CONTENT
   =========================================== */

export interface SideSheetContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Width of the sheet
   * - sm: 400px max width
   * - md: 560px max width (default)
   * - lg: 720px max width
   * - xl: 960px max width
   * - full: Full viewport width
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';

  className?: string;
  children?: React.ReactNode;
}

export const SideSheetContent = React.forwardRef<HTMLDivElement, SideSheetContentProps>(
  ({ size = 'md', className, children, ...props }, ref) => {
    return (
      <BaseDialog.Popup
        ref={ref}
        className={clsx(styles.content, styles[`size-${size}`], className)}
        {...props}
      >
        {children}
      </BaseDialog.Popup>
    );
  }
);

SideSheetContent.displayName = 'SideSheetContent';

/* ===========================================
   SIDE SHEET HEADER
   =========================================== */

export interface SideSheetHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const SideSheetHeader = React.forwardRef<HTMLDivElement, SideSheetHeaderProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={clsx(styles.header, className)} {...props}>
      {children}
    </div>
  )
);

SideSheetHeader.displayName = 'SideSheetHeader';

/* ===========================================
   SIDE SHEET TITLE
   =========================================== */

export interface SideSheetTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  className?: string;
}

export const SideSheetTitle = React.forwardRef<HTMLHeadingElement, SideSheetTitleProps>(
  ({ className, children, ...props }, ref) => (
    <BaseDialog.Title ref={ref} className={clsx(styles.title, className)} {...props}>
      {children}
    </BaseDialog.Title>
  )
);

SideSheetTitle.displayName = 'SideSheetTitle';

/* ===========================================
   SIDE SHEET DESCRIPTION
   =========================================== */

export interface SideSheetDescriptionProps
  extends React.HTMLAttributes<HTMLParagraphElement> {
  className?: string;
}

export const SideSheetDescription = React.forwardRef<
  HTMLParagraphElement,
  SideSheetDescriptionProps
>(({ className, children, ...props }, ref) => (
  <BaseDialog.Description
    ref={ref}
    className={clsx(styles.description, className)}
    {...props}
  >
    {children}
  </BaseDialog.Description>
));

SideSheetDescription.displayName = 'SideSheetDescription';

/* ===========================================
   SIDE SHEET BODY
   =========================================== */

export interface SideSheetBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const SideSheetBody = React.forwardRef<HTMLDivElement, SideSheetBodyProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={clsx(styles.body, className)} {...props}>
      {children}
    </div>
  )
);

SideSheetBody.displayName = 'SideSheetBody';

/* ===========================================
   SIDE SHEET FOOTER
   =========================================== */

export interface SideSheetFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const SideSheetFooter = React.forwardRef<HTMLDivElement, SideSheetFooterProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={clsx(styles.footer, className)} {...props}>
      {children}
    </div>
  )
);

SideSheetFooter.displayName = 'SideSheetFooter';

/* ===========================================
   SIDE SHEET CLOSE
   =========================================== */

export interface SideSheetCloseProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
}

export const SideSheetClose = React.forwardRef<HTMLButtonElement, SideSheetCloseProps>(
  ({ className, children, ...props }, ref) => (
    // aria-label before the spread so an explicit one from the caller wins.
    <BaseDialog.Close
      ref={ref}
      aria-label="Close"
      className={clsx(styles.close, className)}
      {...props}
    >
      {children || (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      )}
    </BaseDialog.Close>
  )
);

SideSheetClose.displayName = 'SideSheetClose';

export default SideSheet;
